/**
 * AiSongSearchDialog — natural-language search across the church song
 * library. Backend LLM ranks hits with a match score + rationale. User
 * can pick the model per-search.
 */
import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Search, Wand2, Sparkles } from 'lucide-react';
import { getToken } from '@/lib/authToken';

const API = process.env.REACT_APP_BACKEND_URL;
const authH = () => ({ Authorization: `Bearer ${getToken()}` });

const MODELS = [
  { key: 'gemini-2.5-flash', label: 'Gemini 2.5 Flash', hint: 'fast · cheap' },
  { key: 'claude-sonnet-4-6', label: 'Claude Sonnet 4.6', hint: 'nuanced' },
  { key: 'gpt-5.4-mini', label: 'GPT 5.4 Mini', hint: 'balanced' },
];

export default function AiSongSearchDialog({ onClose }) {
  const [query, setQuery] = useState('');
  const [model, setModel] = useState(MODELS[0].key);
  const [count, setCount] = useState(8);
  const [result, setResult] = useState(null);

  const search = useMutation({
    mutationFn: async () => (await axios.post(
      `${API}/api/admin/worship/songs/search-ai`,
      { query, count: Number(count) || 8, model },
      { headers: { ...authH(), 'Content-Type': 'application/json' } },
    )).data,
    onSuccess: (d) => { setResult(d); toast.success(`${d.matches.length} matches`); },
    onError: (e) => toast.error(`Search: ${e?.response?.data?.detail || e.message}`),
  });

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent data-testid="ai-song-search-dialog" className="bg-slate-900 border-slate-700 text-slate-100 max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-emerald-300" /> AI Song Search
          </DialogTitle>
          <DialogDescription className="text-slate-400 text-xs">
            Type a Bible reference, theme, or scenario. AI ranks songs from your library only.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
              <Input data-testid="ai-search-query" value={query}
                     onChange={(e) => setQuery(e.target.value)}
                     placeholder="e.g. songs about God's faithfulness for a funeral · Psalm 23 · Christmas morning"
                     className="pl-8 bg-slate-950 border-slate-700"
                     onKeyDown={(e) => { if (e.key === 'Enter' && query.trim()) search.mutate(); }} />
            </div>
            <select data-testid="ai-search-model" value={model} onChange={(e) => setModel(e.target.value)}
                    className="h-9 rounded border border-slate-700 bg-slate-950 px-2 text-xs">
              {MODELS.map((m) => <option key={m.key} value={m.key}>{m.label} ({m.hint})</option>)}
            </select>
            <Input data-testid="ai-search-count" type="number" min={1} max={20} value={count}
                   onChange={(e) => setCount(e.target.value)}
                   className="w-16 bg-slate-950 border-slate-700 text-center" />
            <Button data-testid="ai-search-go" onClick={() => search.mutate()}
                    disabled={!query.trim() || search.isPending}
                    className="bg-emerald-600 hover:bg-emerald-500">
              <Wand2 className="mr-1 h-3 w-3" /> {search.isPending ? 'Searching…' : 'Search'}
            </Button>
          </div>

          {result && (
            <div className="space-y-1" data-testid="ai-search-results">
              {result.matches.length === 0 && (
                <p className="text-xs text-slate-500 text-center py-4">
                  No matching songs in your library. Try a broader theme or add more songs.
                </p>
              )}
              {result.matches.map((m) => (
                <div key={m.song_id}
                     data-testid={`ai-search-hit-${m.song_id}`}
                     className="rounded border border-slate-800 bg-slate-950/70 px-3 py-2">
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-slate-100">{m.title}</span>
                        {m.key && <Badge variant="outline" className="border-slate-700 text-[9px] text-slate-400">key {m.key}</Badge>}
                        {(m.themes || []).slice(0, 3).map((t) => (
                          <Badge key={t} variant="outline" className="border-violet-500/30 text-violet-300 text-[9px]">{t}</Badge>
                        ))}
                      </div>
                      {m.author && <p className="text-[10px] text-slate-500 mt-0.5">{m.author}</p>}
                      <p className="text-[11px] text-emerald-200/80 italic mt-1">
                        <Sparkles className="mr-0.5 inline-block h-3 w-3 opacity-70" /> {m.reason}
                      </p>
                    </div>
                    <div className="shrink-0 text-right border-l border-slate-800 pl-3">
                      <div className="text-2xl font-bold text-emerald-300 tabular-nums leading-none">{m.score}</div>
                      <div className="text-[9px] uppercase tracking-wider text-slate-500 mt-1">match</div>
                    </div>
                  </div>
                </div>
              ))}
              {result.matches.length > 0 && (
                <p className="text-[9px] text-slate-500 mt-1">Model: {result.model}</p>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
